import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { usePortfolioProjects } from '../../../api/summary-client/summary-client.hooks';
import type { AgentAdoptionParams } from '../../../api/agent-miner-client/agent-miner-client.type';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useModelComparisonParams } from '../model-comparison/model-comparison.hooks';
import type { AdoptionTrendMeasure, AdoptionView } from './adoption.type';

/**
 * URL-synced team filter for the Adoption tab, kept as a comma-separated
 * `ad_teams` param. Values are validated against the boot-hydrated global
 * teams list so a hand-edited URL (or a team id shared from another
 * portfolio) can't inject garbage; unknown ids are silently dropped, matching
 * the backend's treatment of inaccessible team_ids. An empty selection means
 * "all teams" and is encoded as param absence.
 *
 * Teams hydrate asynchronously (a portfolio-projects query written into the
 * store), so on a hard refresh onto a `?ad_teams=…` URL the list is briefly
 * empty. Validating against that empty list would collapse the filter to "all
 * teams" and fire an unfiltered request that then refetches once teams land.
 * To avoid that, the URL ids are passed through untouched until the teams
 * query has actually fetched; the backend already ignores inaccessible ids, so
 * an unknown token in that window is harmless and gets dropped once teams
 * resolve.
 *
 * @returns {{ teamIds: string[], setTeamIds: (next: string[]) => void }}
 */
const useAdoptionTeamFilter = (): {
  teamIds: string[];
  setTeamIds: (next: string[]) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const teams = useGlobalStore((state) => state.teams);
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  // Same query key as the app-level teams initializer, so this only reads the
  // shared cache entry's fetch status — it does not trigger a second request.
  const { query: teamsQuery } = usePortfolioProjects(portfolioId, {
    enabled: !!portfolioId,
    staleTime: 1000 * 60 * 5,
  });
  const raw = searchParams.get('ad_teams');
  const teamsHydrated = teamsQuery.isFetched || teams.length > 0;

  const teamIds = useMemo(() => {
    const deduped = Array.from(new Set(raw ? raw.split(',').filter(Boolean) : []));
    // Not yet hydrated: preserve the URL ids rather than dropping them all.
    if (!teamsHydrated) return deduped;
    const knownTeamIds = new Set(teams.map((team) => team.id));
    return deduped.filter((teamId) => knownTeamIds.has(teamId));
  }, [raw, teams, teamsHydrated]);

  const setTeamIds = (next: string[]) => {
    const copy = new URLSearchParams(searchParams);
    if (next.length === 0) copy.delete('ad_teams');
    else copy.set('ad_teams', next.join(','));
    setSearchParams(copy, { replace: true });
  };

  return { teamIds, setTeamIds };
};

/**
 * Resolves the adoption query params: the shared Model Comparison scope
 * (organization, shared URL date window, effective allocation type — so the
 * range and bucket carry across the AI-adoption tabs) plus the selected
 * portfolio (which bounds the team universe to the same projects the filter
 * panel offers) and the adoption-only team filter.
 *
 * @returns {AgentAdoptionParams} Query params for useAgentAdoption
 */
const useAdoptionParams = (): AgentAdoptionParams => {
  const baseParams = useModelComparisonParams();
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  const { teamIds } = useAdoptionTeamFilter();

  return useMemo(
    () => ({
      ...baseParams,
      portfolio_id: portfolioId ?? undefined,
      team_ids: teamIds.length > 0 ? teamIds : undefined,
    }),
    [baseParams, portfolioId, teamIds],
  );
};

/**
 * Trends/teams/attribution view mode for the tab, kept in the `ad_view` URL
 * param so it survives navigation like the rest of the page's filter state.
 * Trends is the default and is encoded as param absence to keep shared URLs
 * clean; an unknown hand-edited value falls back to trends.
 *
 * @returns {{ view: AdoptionView, setView: (view: AdoptionView) => void }}
 */
const useAdoptionView = (): {
  view: AdoptionView;
  setView: (view: AdoptionView) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('ad_view');
  const view: AdoptionView = raw === 'teams' || raw === 'attribution' ? raw : 'trends';

  const setView = (next: AdoptionView) => {
    const copy = new URLSearchParams(searchParams);
    if (next === 'trends') copy.delete('ad_view');
    else copy.set('ad_view', next);
    setSearchParams(copy, { replace: true });
  };

  return { view, setView };
};

/**
 * Which series the Trends chart plots, kept in the `ad_measure` URL param.
 * Active users is the default and therefore omitted from the URL; an unknown
 * hand-edited value falls back to it.
 *
 * @returns {{ measure: AdoptionTrendMeasure, setMeasure: (next: AdoptionTrendMeasure) => void }}
 */
const useAdoptionTrendMeasure = (): {
  measure: AdoptionTrendMeasure;
  setMeasure: (next: AdoptionTrendMeasure) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('ad_measure');
  const measure: AdoptionTrendMeasure = raw === 'sessions' || raw === 'adoption_rate' ? raw : 'active_users';

  const setMeasure = (next: AdoptionTrendMeasure) => {
    const copy = new URLSearchParams(searchParams);
    if (next === 'active_users') copy.delete('ad_measure');
    else copy.set('ad_measure', next);
    setSearchParams(copy, { replace: true });
  };

  return { measure, setMeasure };
};

export { useAdoptionParams, useAdoptionTeamFilter, useAdoptionTrendMeasure, useAdoptionView };
